import { useCallback, useEffect, useRef, useState } from 'react';
import cognitoProvider from '../cognito';
import useAuthStore from '../store/authStore';
import { useEndPartnerImpersonationMutation } from '../api/impersonation';

export const IDLE_WARNING_MS = 15 * 60 * 1000;
export const IDLE_LOGOUT_MS = 16 * 60 * 1000;
const CHECK_INTERVAL_MS = 1000;

const INTERACTION_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'touchstart',
  'wheel',
  'scroll',
  'pointerdown',
  'pointermove',
  'focus',
];

export function useIdleTimeout() {
  const impersonationPartnerId = useAuthStore((s) => s.impersonationPartnerId);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const endPartnerImpersonation = useEndPartnerImpersonationMutation();

  const [warningOpen, setWarningOpen] = useState(false);
  const lastActivity = useRef(Date.now());
  const loggedOut = useRef(false);

  const logOut = useCallback(async () => {
    if (loggedOut.current) {
      return;
    }
    loggedOut.current = true;

    if (impersonationPartnerId) {
      await endPartnerImpersonation.mutateAsync(impersonationPartnerId);
    }
    await cognitoProvider.signOut();
    clearAuth();
    window.location.href = '/sign-in?showIdleTimeoutNotification';
  }, [impersonationPartnerId, endPartnerImpersonation, clearAuth]);

  const continueSession = useCallback(() => {
    lastActivity.current = Date.now();
    setWarningOpen(false);
  }, []);

  useEffect(() => {
    const handleActivity = () => {
      lastActivity.current = Date.now();
    };

    INTERACTION_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      INTERACTION_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const idleFor = Date.now() - lastActivity.current;

      if (idleFor > IDLE_LOGOUT_MS) {
        clearInterval(interval);
        logOut();
        return;
      }

      if (idleFor > IDLE_WARNING_MS) {
        setWarningOpen(true);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [logOut]);

  return { warningOpen, continueSession, logOut };
}
